import type { FC, ReactNode } from "react";
import { Button, Card, SimpleGrid, Stack, Text } from "@mantine/core";
import {
  AddressDisplay,
  getPatientLabelFirstLine,
  getPatientLabelSecondLine,
  type Order,
} from "@jasper/shared";
import type { OrderPageState } from "../useOrderPage";
import {
  formatDayMonth,
  getProductHeadline,
  getProductsCountLabel,
} from "./order-page-v2.utils";

type RibbonItemProps = {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
};

const RibbonItem: FC<RibbonItemProps> = ({ label, value, meta }) => (
  <Stack bg="cardBackground" px="s5" py="s4" gap="s2">
    <Text variant="overline">{label}</Text>
    <div>
      <Text variant="strong">{value}</Text>
      {meta && (
        <Text variant="caption" component="div">
          {meta}
        </Text>
      )}
    </div>
  </Stack>
);

type OrderInfoRibbonProps = {
  order: Order;
  expectedDeliveryDate: Date;
  state: OrderPageState;
};

export const OrderInfoRibbon: FC<OrderInfoRibbonProps> = ({
  order,
  expectedDeliveryDate,
  state,
}) => (
  <Card withBorder p={0} bg="ink.1">
    <SimpleGrid
      cols={{ base: 1, sm: 2, md: 4 }}
      spacing={1}
      verticalSpacing={1}
    >
      <RibbonItem
        label="Patient"
        value={getPatientLabelFirstLine(order)}
        meta={getPatientLabelSecondLine(order)}
      />
      <RibbonItem
        label="Prothèses"
        value={getProductsCountLabel(order.products)}
        meta={order.products.map(getProductHeadline).join(", ")}
      />
      <RibbonItem
        label="Livraison estimée"
        value={formatDayMonth(expectedDeliveryDate)}
        meta={
          state.permissions.canShowCarrierDeliveryDetails && (
            <Button
              variant="subtle"
              size="sm"
              px={0}
              onClick={state.modals.carrierDelivery.open}
            >
              Voir les détails de livraison
            </Button>
          )
        }
      />
      <RibbonItem
        label="Praticien"
        value={`${order.user.firstName} ${order.user.lastName}`}
        meta={<AddressDisplay address={order.user.userGroup?.addresses?.[0]} />}
      />
    </SimpleGrid>
  </Card>
);
